import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import {
  FileText,
  Plus,
  Search,
  Eye,
  Trash2,
  Loader2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Calendar,
  DollarSign,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const Invoices = () => {
  const { token, isManager } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [pendingReview, setPendingReview] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [deleteInvoice, setDeleteInvoice] = useState(null);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [targetMonth, setTargetMonth] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [invoicesRes, pendingRes] = await Promise.all([
        axios.get(`${API}/invoices`, { headers }),
        axios.get(`${API}/invoices/pending-review`, { headers })
      ]);
      setInvoices(invoicesRes.data);
      setPendingReview(pendingRes.data);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Set default target month to next month
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];
    setTargetMonth(`${monthNames[nextMonth.getMonth()]} ${nextMonth.getFullYear()}`);
  }, [token]);

  const handleViewInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}`, { headers });
      setViewingInvoice(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load invoice");
    }
  };

  const handleGenerateInvoices = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/invoices/generate-monthly?target_month=${encodeURIComponent(targetMonth)}`,
        {},
        { headers }
      );
      toast.success(response.data.message);
      setGenerateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate invoices");
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/approve`, {}, { headers });
      toast.success("Invoice approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve invoice");
    }
  };

  const handleWaiveLateFee = async (invoiceId) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/waive-late-fee`, {}, { headers });
      toast.success("Late fee waived");
      fetchData();
      if (viewingInvoice?.id === invoiceId) {
        const response = await axios.get(`${API}/invoices/${invoiceId}`, { headers });
        setViewingInvoice(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to waive late fee");
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    setSending(invoiceId);
    try {
      const response = await axios.put(`${API}/invoices/${invoiceId}/send`, {}, { headers });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invoice");
    } finally {
      setSending(null);
    }
  };

  const handleStatusChange = async (invoiceId, status) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/status?status=${status}`, {}, { headers });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/invoices/${deleteInvoice.id}`, { headers });
      toast.success("Invoice deleted");
      setDeleteInvoice(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_review: { class: "bg-yellow-50 text-yellow-700", icon: Clock },
      approved: { class: "bg-blue-100 text-blue-700", icon: CheckCircle },
      sent: { class: "bg-green-100 text-green-700", icon: Send },
      paid: { class: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
      overdue: { class: "bg-red-100 text-red-700", icon: AlertTriangle }
    };
    return styles[status] || styles.pending_review;
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invoice.tenant?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    invoice.rent_period?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingCount = invoices.filter(i => i.status === "pending_review").length;
  const approvedCount = invoices.filter(i => i.status === "approved").length;

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="skeleton h-96 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="invoices-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="text-slate-500 mt-1">
            {pendingCount > 0 && <span className="text-yellow-600 font-medium">{pendingCount} pending review</span>}
            {pendingCount > 0 && approvedCount > 0 && " • "}
            {approvedCount > 0 && <span className="text-blue-600 font-medium">{approvedCount} ready to send</span>}
          </p>
        </div>
        {isManager && (
          <div className="flex gap-2">
            <Button 
              onClick={() => setGenerateDialogOpen(true)} 
              className="bg-green-700 hover:bg-green-800"
              data-testid="generate-invoices-button"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Monthly Invoices
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {isManager && (pendingCount > 0 || approvedCount > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {pendingCount > 0 && (
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{pendingCount} Invoices Pending Review</p>
                      <p className="text-sm text-slate-500">Review before they auto-send on the 1st</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setActiveTab("pending")}
                  >
                    Review Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {approvedCount > 0 && (
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <Send className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">{approvedCount} Ready to Send</p>
                      <p className="text-sm text-slate-500">Approved and waiting to be sent</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="all">All ({invoices.length})</TabsTrigger>
            <TabsTrigger value="pending" className="relative">
              Pending Review
              {pendingCount > 0 && (
                <span className="ml-2 bg-yellow-500 text-white text-xs rounded-full px-2 py-0.5">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent">Sent</TabsTrigger>
          </TabsList>

          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="invoice-search-input"
            />
          </div>
        </div>

        <TabsContent value="all">
          <InvoiceTable 
            invoices={filteredInvoices}
            isManager={isManager}
            onView={handleViewInvoice}
            onApprove={handleApprove}
            onSend={handleSendInvoice}
            onStatusChange={handleStatusChange}
            onDelete={setDeleteInvoice}
            sending={sending}
            getStatusBadge={getStatusBadge}
          />
        </TabsContent>

        <TabsContent value="pending">
          <InvoiceTable 
            invoices={pendingReview}
            isManager={isManager}
            onView={handleViewInvoice}
            onApprove={handleApprove}
            onSend={handleSendInvoice}
            onStatusChange={handleStatusChange}
            onDelete={setDeleteInvoice}
            onWaiveLateFee={handleWaiveLateFee}
            sending={sending}
            getStatusBadge={getStatusBadge}
            showLateFeeActions
          />
        </TabsContent>

        <TabsContent value="sent">
          <InvoiceTable 
            invoices={filteredInvoices.filter(i => i.status === "sent" || i.status === "paid")}
            isManager={isManager}
            onView={handleViewInvoice}
            onApprove={handleApprove}
            onSend={handleSendInvoice}
            onStatusChange={handleStatusChange}
            onDelete={setDeleteInvoice}
            sending={sending}
            getStatusBadge={getStatusBadge}
          />
        </TabsContent>
      </Tabs>

      {/* Generate Invoices Dialog */}
      <Dialog open={generateDialogOpen} onOpenChange={setGenerateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Generate Monthly Invoices</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will generate rent invoices for all active leases. Invoices will be set to "Pending Review" status.
            </p>
            
            <div className="space-y-2">
              <Label>Rent Period</Label>
              <Input
                value={targetMonth}
                onChange={(e) => setTargetMonth(e.target.value)}
                placeholder="e.g., February 2025"
                data-testid="target-month-input"
              />
              <p className="text-xs text-slate-500">Format: "January 2025"</p>
            </div>

            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2">What will happen:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Invoices generated for all active leases</li>
                <li>• Late fees auto-added if payment overdue (after 7th)</li>
                <li>• Status set to "Pending Review"</li>
                <li>• Review by 31st, auto-sends on 1st if not reviewed</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateInvoices} 
              className="bg-green-700 hover:bg-green-800"
              disabled={generating}
              data-testid="confirm-generate-button"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate Invoices"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog - Style 2 (Modern Green) */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {viewingInvoice && (
            <div className="bg-white rounded-xl overflow-hidden">
              {/* Green Header - Shows OWNER name (the property owner) */}
              <div className="bg-green-700 text-white p-8">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="text-xl font-bold">{viewingInvoice.owner?.name || "Property Owner"}</span>
                        {viewingInvoice.owner?.owner_type === "llc" && (
                          <span className="ml-2 text-xs bg-white/20 px-2 py-0.5 rounded">LLC</span>
                        )}
                      </div>
                    </div>
                    <p className="text-green-100 text-sm">{viewingInvoice.property?.address}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-200 text-sm mb-1">Invoice</p>
                    <p className="text-2xl font-bold">{viewingInvoice.invoice_number}</p>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Bill To & Dates */}
                <div className="flex justify-between mb-8 pb-6 border-b border-slate-100">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-green-700 mb-2">Billed To</p>
                    <p className="font-semibold text-slate-900 text-lg">{viewingInvoice.tenant?.name}</p>
                    <p className="text-slate-500 text-sm">{viewingInvoice.property?.address}</p>
                  </div>
                  <div className="text-right">
                    <div className="mb-3">
                      <p className="text-xs text-slate-400">Rent Period</p>
                      <p className="font-medium text-slate-700">{viewingInvoice.rent_period}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Due Date</p>
                      <p className="font-medium text-slate-700">{viewingInvoice.due_date}</p>
                    </div>
                  </div>
                </div>

                {/* Late Fee Alert */}
                {viewingInvoice.late_fee_applied && !viewingInvoice.late_fee_waived && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <div>
                        <p className="font-medium text-red-800">Late Fee Applied</p>
                        <p className="text-sm text-red-600">Payment was received after grace period</p>
                      </div>
                    </div>
                    {isManager && viewingInvoice.status === "pending_review" && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleWaiveLateFee(viewingInvoice.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Waive Fee
                      </Button>
                    )}
                  </div>
                )}

                {viewingInvoice.late_fee_waived && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <p className="font-medium text-green-800">Late fee was waived</p>
                  </div>
                )}

                {/* Items */}
                <div className="mb-8">
                  <div className="grid grid-cols-12 gap-4 text-xs font-semibold uppercase tracking-wider text-slate-400 pb-3 border-b border-slate-100">
                    <div className="col-span-6">Item</div>
                    <div className="col-span-2 text-center">Qty</div>
                    <div className="col-span-2 text-right">Price</div>
                    <div className="col-span-2 text-right">Total</div>
                  </div>
                  {viewingInvoice.items?.map((item, i) => (
                    <div 
                      key={i} 
                      className={`grid grid-cols-12 gap-4 py-4 border-b border-slate-50 ${
                        item.item_type === 'late_fee' ? 'bg-red-50 -mx-4 px-4' : ''
                      }`}
                    >
                      <div className="col-span-6 text-slate-700 flex items-center gap-2">
                        {item.item_type === 'late_fee' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                        {item.description}
                      </div>
                      <div className="col-span-2 text-center text-slate-500">{item.quantity}</div>
                      <div className="col-span-2 text-right text-slate-500 tabular-nums">${item.unit_price?.toFixed(2)}</div>
                      <div className="col-span-2 text-right font-medium text-slate-800 tabular-nums">${item.amount?.toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                {/* Total Box */}
                <div className="flex justify-end">
                  <div className="bg-green-50 rounded-xl p-6 w-72">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="text-slate-700 tabular-nums">${viewingInvoice.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-4 border-t border-green-200">
                      <span className="font-semibold text-green-800">Total Due</span>
                      <span className="text-2xl font-bold text-green-700 tabular-nums">${viewingInvoice.total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {viewingInvoice.notes && (
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Payment Instructions</p>
                    <p className="text-sm text-slate-500">{viewingInvoice.notes}</p>
                  </div>
                )}

                {/* Management Company Footer */}
                <div className="mt-6 pt-4 border-t border-slate-200 text-center">
                  <p className="text-xs text-slate-400">Managed by</p>
                  <p className="text-sm font-medium text-green-700">AV Property Management</p>
                </div>
              </div>

              {/* Footer Actions */}
              {isManager && (
                <div className="border-t border-slate-200 p-4 bg-slate-50 flex justify-end gap-2">
                  {viewingInvoice.status === "pending_review" && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => {
                          handleApprove(viewingInvoice.id);
                          setViewDialogOpen(false);
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button 
                        className="bg-green-700 hover:bg-green-800"
                        onClick={() => {
                          handleSendInvoice(viewingInvoice.id);
                          setViewDialogOpen(false);
                        }}
                        disabled={sending === viewingInvoice.id}
                      >
                        {sending === viewingInvoice.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Approve & Send
                      </Button>
                    </>
                  )}
                  {viewingInvoice.status === "approved" && (
                    <Button 
                      className="bg-green-700 hover:bg-green-800"
                      onClick={() => {
                        handleSendInvoice(viewingInvoice.id);
                        setViewDialogOpen(false);
                      }}
                      disabled={sending === viewingInvoice.id}
                    >
                      {sending === viewingInvoice.id ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Invoice
                    </Button>
                  )}
                  {viewingInvoice.status === "sent" && (
                    <Button 
                      variant="outline"
                      onClick={() => handleStatusChange(viewingInvoice.id, "paid")}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Paid
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteInvoice} onOpenChange={() => setDeleteInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice {deleteInvoice?.invoice_number}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Invoice Table Component
const InvoiceTable = ({ 
  invoices, 
  isManager, 
  onView, 
  onApprove, 
  onSend, 
  onStatusChange, 
  onDelete,
  onWaiveLateFee,
  sending,
  getStatusBadge,
  showLateFeeActions 
}) => {
  if (invoices.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-slate-400" />
        </div>
        <p className="text-slate-500">No invoices found</p>
      </div>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Invoice</TableHead>
            <TableHead>Tenant</TableHead>
            <TableHead>Rent Period</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead className="text-right">Total</TableHead>
            <TableHead>Status</TableHead>
            {isManager && <TableHead className="w-40">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((invoice) => {
            const statusStyle = getStatusBadge(invoice.status);
            const StatusIcon = statusStyle.icon;
            
            return (
              <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                <TableCell>
                  <div>
                    <p className="font-medium">{invoice.invoice_number}</p>
                    {invoice.late_fee_applied && !invoice.late_fee_waived && (
                      <Badge className="bg-red-100 text-red-700 text-xs mt-1">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Late Fee
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>{invoice.tenant?.name || "N/A"}</TableCell>
                <TableCell className="font-medium">{invoice.rent_period}</TableCell>
                <TableCell>{invoice.due_date}</TableCell>
                <TableCell className="text-right font-semibold tabular-nums">
                  ${invoice.total?.toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge className={statusStyle.class}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {invoice.status?.replace("_", " ")}
                  </Badge>
                </TableCell>
                {isManager && (
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onView(invoice)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {invoice.status === "pending_review" && (
                        <>
                          {showLateFeeActions && invoice.late_fee_applied && !invoice.late_fee_waived && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-orange-600"
                              onClick={() => onWaiveLateFee(invoice.id)}
                              title="Waive late fee"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-green-600"
                            onClick={() => onSend(invoice.id)}
                            disabled={sending === invoice.id}
                            title="Approve & Send"
                          >
                            {sending === invoice.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                        </>
                      )}
                      {invoice.status === "approved" && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-green-600"
                          onClick={() => onSend(invoice.id)}
                          disabled={sending === invoice.id}
                        >
                          {sending === invoice.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600" 
                        onClick={() => onDelete(invoice)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
};

export default Invoices;
